import styled, { css } from 'styled-components';

import { BAR_COLORS, COLORS, DELTA_PILL, SPACE, STATS_STACK } from '../../../theme/tokens';
import { Text } from '../../../components';

/**
 * Page-level layout for one mailbox's Setup Mailbox page (Mailbox). The
 * back band sits OUTSIDE the panel; the panel stacks the header, the credential block, the three
 * stats, the client guide and the webmail block, one SPACE[20] step apart.
 */
export const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACE[20]};
`;

/** The header: the address and its pill on the left; the state action and the trash on the right.
 *  Wraps so a long address never squeezes the actions. */
export const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${SPACE[12]};
  flex-wrap: wrap;
`;

/** The header's left group: the address, then a pill only for the non-billing states that need
 *  naming. Takes the row's slack; the right-hand action group keeps its own geometry. */
export const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: ${SPACE[8]};
  flex: 1 1 auto;
  min-width: 0;
`;

/** The full address as the page's own heading. Shrinks (min-width: 0) so a long address gives way
 *  to the pill beside it, which stays whole. */
export const Address = styled.h2`
  margin: 0;
  flex: 1 1 auto;
  min-width: 0;
  font-size: 20px;
  font-weight: 700;
  line-height: 28px;
  color: ${COLORS.textStrong};
  overflow-wrap: anywhere;
`;

/** The right-hand cluster: the state action (or nothing) and the trash. */
export const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${SPACE[8]};
  flex: 0 0 auto;
`;

const linkBase = css`
  appearance: none;
  display: inline-flex;
  align-items: center;
  /* Off-scale: 6px is the label->glyph micro-gap of a link, glyph geometry not layout. */
  gap: 6px;
  padding: 0 ${SPACE[12]};
  height: 36px;
  border-radius: 99px;
  cursor: pointer;
  font-size: 13px;
  font-weight: 600;
  line-height: 19.5px;
  text-decoration: none;
  white-space: nowrap;
`;

/** Webmail: the product's primary verb, so it gets the filled treatment. A button, not an anchor:
 *  webmail signs in by a form POST (see `webmail.ts`), there is no URL. Disabled on a deleted
 *  mailbox, where it reads as an outline with faint text. */
export const WebmailButton = styled.button`
  ${linkBase}
  border: 1px solid ${COLORS.ctaSolid};
  background: ${COLORS.ctaSolid};
  color: ${COLORS.ctaSolidText};

  &:disabled {
    cursor: not-allowed;
    border-color: ${COLORS.hoverBorderLit};
    background: none;
    color: ${COLORS.textFaint};
  }
`;

/** Delete: an icon-only button in the header's action cluster. MUTED at rest (a destructive control
 *  that shouts on every visit stops being a signal); danger on hover and on focus, ring included.
 *  No less destructive: it just stops pre-announcing it. */
export const TrashButton = styled.button`
  appearance: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  padding: 0;
  border-radius: 99px;
  border: 1px solid ${COLORS.hoverBorderLit};
  background: none;
  color: ${COLORS.textMuted};
  cursor: pointer;

  &:hover,
  &:focus-visible {
    color: ${COLORS.danger};
    border-color: ${COLORS.danger};
    background: ${COLORS.hoverWash};
  }

  &:focus-visible {
    outline: 2px solid ${COLORS.danger};
    outline-offset: 2px;
  }
`;

/** The pill beside the address: a provider status at a glance (suspended, deleted). `flex: 0 0 auto`
 *  so a long address gives way and the pill stays whole. */
export const StatusPill = styled.span<{ $bg: string; $fg: string }>`
  display: inline-flex;
  align-items: center;
  flex: 0 0 auto;
  /* Off-scale by design: the pill's internal geometry against its 24px height. A pill is a word and
     its colour: no glyph. */
  height: 24px;
  padding: 0 10px;
  border-radius: 99px;
  font-size: 11px;
  font-weight: 700;
  white-space: nowrap;
  color: ${(p) => p.$fg};
  background: ${(p) => p.$bg};
`;

/** The three stats, one column each; they stack below STATS_STACK. */
export const Stats = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: ${SPACE[12]};

  ${STATS_STACK} {
    grid-template-columns: minmax(0, 1fr);
  }
`;

/** One stat card: label, value, sub-line (or the storage bar). */
export const Stat = styled.div`
  display: flex;
  flex-direction: column;
  /* Off-scale: 3px is the tight label->value pairing of one field. */
  gap: 3px;
  min-width: 0;
  padding: ${SPACE[12]} ${SPACE[16]};
  border-radius: 12px;
  background: ${COLORS.surfaceFaint};
`;

/** The Plan stat: POSITIONED, because it hosts the info glyph in its top-right corner (the
 *  tooltip's containing block), and its right padding reserves that corner (the 18px glyph plus a
 *  step) so the value never runs under it. */
export const PlanStat = styled(Stat)`
  position: relative;
  padding-right: calc(${SPACE[16]} + 18px + ${SPACE[8]});
`;

export const StatLabel = styled(Text)`
  font-size: 11px;
  line-height: 16.5px;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: ${COLORS.textFaint};
`;

export const StatValue = styled.span`
  font-size: 16px;
  font-weight: 700;
  line-height: 24px;
  color: ${COLORS.textStrong};
  font-variant-numeric: tabular-nums;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const StatSub = styled(Text)`
  font-size: 12px;
  line-height: 18px;
  color: ${COLORS.textMuted};
`;

/** The Messages card's ONE line under the count: its halves (when last mail arrived, unread mail
 *  waiting) in the stat sub-line's type, joined by a muted dot. */
export const StatLine = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  column-gap: ${SPACE[4]};
  font-size: 12px;
  line-height: 18px;
  color: ${COLORS.textMuted};
`;

/** The storage bar inside the Storage stat: same flat 12px treatment as the tile's. */
export const Bar = styled.div`
  position: relative;
  height: 12px;
  width: 100%;
  margin-top: ${SPACE[4]};
  overflow: hidden;
  background: ${COLORS.surfaceMuted};
`;

export const BarFill = styled.div<{ $ratio: number; $tone: 'info' | 'warning' }>`
  position: absolute;
  inset: 0 auto 0 0;
  width: ${(p) => Math.round(p.$ratio * 1000) / 10}%;
  background: ${(p) => BAR_COLORS[p.$tone]};
`;

/** A titled block (the client guide): heading, then its body. */
export const Section = styled.section`
  display: flex;
  flex-direction: column;
  gap: ${SPACE[12]};
`;

export const SectionHeading = styled(Text)`
  font-size: 15px;
  font-weight: 700;
  line-height: 22px;
  color: ${COLORS.textStrong};
`;

/** Loading placeholder: skeleton blocks in the panel's rhythm. */
export const SkelStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACE[16]};
`;

/** Pill tokens for the header, per provider status that gets a pill (the same choices as the tile's). */
export const PILL_TONES = {
  suspended: { bg: COLORS.surfaceCard, fg: COLORS.textMuted },
  deleted: { bg: DELTA_PILL.negative.bg, fg: DELTA_PILL.negative.fg },
} as const;
